/**
 * Edge Function通信の標準化クライアント
 * FL-127で得た知見を活用した統一的なEdge Function呼び出し機能
 */

import { getSupabaseClient } from '../../utils/supabase';
import { AppError, ErrorCategory, ErrorLevel } from '../../utils/errorHandler';
import logger from '../../utils/logger';
import { withRetry } from '../../utils/retryHandler';

// =============================================================================
// 型定義
// =============================================================================

export interface EdgeFunctionOptions {
  /** タイムアウト時間（ミリ秒）デフォルト: 30秒 */
  timeout?: number;
  /** リトライ回数（デフォルト: 3回） */
  retries?: number;
  /** 進捗コールバック */
  onProgress?: (message: string) => void;
  /** リクエストID（追跡用） */
  requestId?: string;
  /** オペレーションID（ロギング用） */
  operationId?: string;
}

export interface EdgeFunctionResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code?: string;
    message: string;
    details?: any;
  };
}

export interface EdgeFunctionMetrics {
  functionName: string;
  duration: number;
  success: boolean;
  retryCount: number;
  requestId: string;
  timestamp: string;
}

// =============================================================================
// EdgeFunctionClient クラス
// =============================================================================

/**
 * Edge Function呼び出しの統一クライアント
 *
 * 機能:
 * - 統一されたエラーハンドリング
 * - 自動リトライ機能
 * - 構造化ログ
 * - パフォーマンス測定
 * - タイムアウト制御
 */
export class EdgeFunctionClient {
  private static instance: EdgeFunctionClient | null = null;
  private metrics: Map<string, EdgeFunctionMetrics> = new Map();

  private constructor() {
    logger.setDefaultContext({
      component: 'EdgeFunctionClient',
      file: 'EdgeFunctionClient.ts',
    });
  }

  public static getInstance(): EdgeFunctionClient {
    if (!EdgeFunctionClient.instance) {
      EdgeFunctionClient.instance = new EdgeFunctionClient();
    }
    return EdgeFunctionClient.instance;
  }

  /**
   * Edge Functionを呼び出す
   */
  async invoke<TResponse = any, TPayload = any>(
    functionName: string,
    payload: TPayload,
    options: EdgeFunctionOptions = {}
  ): Promise<EdgeFunctionResponse<TResponse>> {
    const {
      timeout = 30000,
      retries = 3,
      onProgress,
      requestId = this.generateRequestId(),
      operationId = logger.startOperation(`edge-function-${functionName}`, {
        functionName,
        requestId,
      }),
    } = options;

    const startTime = Date.now();

    logger.api(
      'Edge Function invocation started',
      {
        functionName,
        requestId,
        payload: this.sanitizePayload(payload),
        options: { timeout, retries },
      },
      {
        operationId,
        context: { function: 'invoke' },
        tags: ['edge-function', 'start'],
      }
    );

    try {
      const result = await withRetry(
        () => this.executeFunction(functionName, payload, timeout, requestId),
        {
          maxRetries: retries,
          baseDelay: 1000,
          onRetry: (attempt, error) => {
            logger.warn(
              'Edge Function retry attempt',
              {
                functionName,
                requestId,
                attempt,
                error: error.message,
              },
              {
                operationId,
                context: { function: 'invoke' },
                tags: ['edge-function', 'retry'],
              }
            );

            onProgress?.(`再試行中 (${attempt}/${retries})...`);
          },
        }
      );

      const duration = Date.now() - startTime;

      // メトリクス記録
      this.recordMetrics({
        functionName,
        duration,
        success: true,
        retryCount: 0, // withRetryが成功時のリトライカウントを返すように改善が必要
        requestId,
        timestamp: new Date().toISOString(),
      });

      logger.endOperation(operationId, `edge-function-${functionName}`, duration, {
        success: true,
        requestId,
      });

      logger.api(
        'Edge Function invocation completed',
        {
          functionName,
          requestId,
          duration,
          success: result.success,
        },
        {
          operationId,
          context: { function: 'invoke' },
          tags: ['edge-function', 'success'],
        }
      );

      return result as EdgeFunctionResponse<TResponse>;
    } catch (error) {
      const duration = Date.now() - startTime;

      // メトリクス記録
      this.recordMetrics({
        functionName,
        duration,
        success: false,
        retryCount: retries,
        requestId,
        timestamp: new Date().toISOString(),
      });

      const appError = this.handleError(error, functionName, requestId);

      logger.error(
        'Edge Function invocation failed',
        {
          functionName,
          requestId,
          duration,
          error: appError.message,
          originalError: error instanceof Error ? error.message : 'Unknown error',
        },
        {
          operationId,
          context: { function: 'invoke' },
          tags: ['edge-function', 'error'],
        }
      );

      return {
        success: false,
        error: {
          code: appError.category,
          message: appError.message,
          details: appError.metadata,
        },
      };
    }
  }

  /**
   * Edge Function実行の実装
   */
  private async executeFunction<TResponse, TPayload>(
    functionName: string,
    payload: TPayload,
    timeout: number,
    requestId: string
  ): Promise<EdgeFunctionResponse<TResponse>> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new AppError(
        'Supabaseクライアントが初期化されていません',
        ErrorCategory.EXTERNAL_API,
        ErrorLevel.HIGH,
        { metadata: { functionName, requestId } }
      );
    }

    try {
      // タイムアウト制御
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(
            new AppError(
              `Edge Function '${functionName}' がタイムアウトしました (${timeout}ms)`,
              ErrorCategory.TIMEOUT,
              ErrorLevel.HIGH,
              { metadata: { functionName, requestId, timeout } }
            )
          );
        }, timeout);
      });

      const functionPromise = supabase.functions.invoke(functionName, {
        body: payload as any,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { data, error } = await Promise.race([functionPromise, timeoutPromise]);

      if (error) {
        throw new AppError(
          `Edge Function '${functionName}' でエラーが発生しました: ${error.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          {
            metadata: {
              functionName,
              requestId,
              supabaseError: error,
            },
            originalError: error,
          }
        );
      }

      if (!data) {
        throw new AppError(
          `Edge Function '${functionName}' からレスポンスがありませんでした`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { metadata: { functionName, requestId } }
        );
      }

      // レスポンス形式の標準化
      if (data && typeof data === 'object' && 'success' in data) {
        return data as EdgeFunctionResponse<TResponse>;
      }

      // 旧形式のレスポンスを新形式に変換
      return {
        success: true,
        data: data as TResponse,
      };
    } catch (primaryError) {
      // Supabase SDK経由で失敗した場合、fetch APIを使って直接呼び出しを試行
      logger.warn(
        'Supabase SDK call failed, attempting direct fetch',
        {
          functionName,
          requestId,
          primaryError: primaryError instanceof Error ? primaryError.message : 'Unknown error',
        },
        {
          context: { function: 'executeFunction' },
          tags: ['edge-function', 'fallback'],
        }
      );

      return await this.executeFunctionWithFetch(functionName, payload, timeout, requestId);
    }
  }

  /**
   * fetch APIを使用したEdge Function実行（フォールバック）
   */
  private async executeFunctionWithFetch<TResponse, TPayload>(
    functionName: string,
    payload: TPayload,
    timeout: number,
    requestId: string
  ): Promise<EdgeFunctionResponse<TResponse>> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new AppError(
        'Supabaseクライアントが初期化されていません',
        ErrorCategory.EXTERNAL_API,
        ErrorLevel.HIGH,
        { metadata: { functionName, requestId } }
      );
    }

    // Supabaseクライアントから設定を取得
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !anonKey) {
      throw new AppError('Supabase設定が不完全です', ErrorCategory.EXTERNAL_API, ErrorLevel.HIGH, {
        metadata: { functionName, requestId },
      });
    }

    const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`;

    try {
      // タイムアウト制御
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new AppError(
          `Edge Function '${functionName}' でHTTPエラーが発生しました: ${response.status} ${response.statusText}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          {
            metadata: {
              functionName,
              requestId,
              status: response.status,
              statusText: response.statusText,
              responseBody: errorText,
            },
          }
        );
      }

      let data: any;

      try {
        const responseText = await response.text();
        if (responseText) {
          data = JSON.parse(responseText);
        } else {
          // 空のレスポンスの場合
          data = { success: true };
        }
      } catch (parseError) {
        // JSONパースエラーの場合、レスポンステキストをそのまま使用
        const responseText = await response.text().catch(() => '');
        logger.warn(
          'Edge Function response parsing failed, using text response',
          {
            functionName,
            requestId,
            responseText,
            parseError: parseError instanceof Error ? parseError.message : 'Parse error',
          },
          {
            context: { function: 'executeFunctionWithFetch' },
            tags: ['edge-function', 'parse-error'],
          }
        );

        return {
          success: true,
          data: responseText as TResponse,
        };
      }

      // レスポンス形式の標準化
      if (data && typeof data === 'object' && 'success' in data) {
        return data as EdgeFunctionResponse<TResponse>;
      }

      // 旧形式のレスポンスを新形式に変換
      return {
        success: true,
        data: data as TResponse,
      };
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }

      if (error && typeof error === 'object' && (error as Error).name === 'AbortError') {
        throw new AppError(
          `Edge Function '${functionName}' がタイムアウトしました (${timeout}ms)`,
          ErrorCategory.TIMEOUT,
          ErrorLevel.HIGH,
          { metadata: { functionName, requestId, timeout } }
        );
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : 'Unknown error';

      throw new AppError(
        `Edge Function '${functionName}' の直接呼び出しでエラーが発生しました: ${errorMessage}`,
        ErrorCategory.EXTERNAL_API,
        ErrorLevel.HIGH,
        {
          metadata: { functionName, requestId },
          originalError: error instanceof Error ? error : new Error(String(error)),
        }
      );
    }
  }

  /**
   * エラーハンドリングの統一処理
   */
  private handleError(error: unknown, functionName: string, requestId: string): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      // ネットワークエラー
      if (error.message && error.message.includes('fetch')) {
        return new AppError(
          `Edge Function '${functionName}' への通信に失敗しました`,
          ErrorCategory.NETWORK,
          ErrorLevel.HIGH,
          {
            metadata: { functionName, requestId },
            originalError: error,
          }
        );
      }

      // その他のエラー
      return new AppError(
        `Edge Function '${functionName}' で予期しないエラーが発生しました: ${error.message || 'Error details unavailable'}`,
        ErrorCategory.EXTERNAL_API,
        ErrorLevel.HIGH,
        {
          metadata: { functionName, requestId },
          originalError: error,
        }
      );
    }

    // 不明なエラー
    return new AppError(
      `Edge Function '${functionName}' で不明なエラーが発生しました`,
      ErrorCategory.UNKNOWN,
      ErrorLevel.HIGH,
      {
        metadata: { functionName, requestId, error: String(error) },
      }
    );
  }

  /**
   * ペイロードのサニタイズ（ログ用）
   */
  private sanitizePayload(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    const sanitized = { ...payload };

    // センシティブな情報をマスク
    const sensitiveFields = ['password', 'token', 'apiKey', 'secret'];

    for (const field of sensitiveFields) {
      if (field in sanitized) {
        sanitized[field] = '***MASKED***';
      }
    }

    return sanitized;
  }

  /**
   * メトリクス記録
   */
  private recordMetrics(metrics: EdgeFunctionMetrics): void {
    this.metrics.set(`${metrics.requestId}-${Date.now()}`, metrics);

    // 古いメトリクスのクリーンアップ（最新1000件を保持）
    if (this.metrics.size > 1000) {
      const entries = Array.from(this.metrics.entries());
      const oldEntries = entries.slice(0, entries.length - 1000);

      for (const [key] of oldEntries) {
        this.metrics.delete(key);
      }
    }

    // パフォーマンスログ
    logger.performance(
      'Edge Function performance',
      {
        duration: metrics.duration,
      },
      {
        functionName: metrics.functionName,
        requestId: metrics.requestId,
        success: metrics.success,
      }
    );
  }

  /**
   * リクエストID生成
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * メトリクス取得
   */
  getMetrics(functionName?: string): EdgeFunctionMetrics[] {
    const allMetrics = Array.from(this.metrics.values());

    if (functionName) {
      return allMetrics.filter(m => m.functionName === functionName);
    }

    return allMetrics;
  }

  /**
   * メトリクスクリア
   */
  clearMetrics(): void {
    this.metrics.clear();
  }

  /**
   * ヘルスチェック
   */
  async healthCheck(): Promise<boolean> {
    try {
      const result = await this.invoke(
        'health-check',
        {},
        {
          timeout: 5000,
          retries: 1,
        }
      );

      return result.success;
    } catch {
      return false;
    }
  }
}

// デフォルトエクスポート
export default EdgeFunctionClient.getInstance();
